/**
 * @author Angel Labrada
 * @since v0.0.1
 * @date 9/12/21
 */
import React, {memo, useState, useCallback, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {Button, Input, List, Upload} from 'antd';
import {
  FlagOutlined,
  EditOutlined,
  PaperClipOutlined
  // PictureOutlined
} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import styles from './index.less';
import incidentStyles from '../WrapperManageTemplateSections/components/ManageTemplateSections/components/QuestionOptions/AddIncidentOrIncidentList/index.less';
import TemplateDetailsIssuesHeader from '@/modules/templates/components/TemplateDetailsIssuesHeader';
import GlobalNote from '@/modules/templates/components/TemplateDetailsFooterActions/components/GlobalNote';
import GlobalImages from '@/modules/templates/components/TemplateDetailsFooterActions/components/GlobalImages';
import useUploadImage from '@/components/UploadImage/useUploadImage';
import { v4 as uuidv4 } from 'uuid';
import {useCrudImage, useCrudNote} from '@/modules/auditorAudits/hooks/useAuditorAudits';
import {useParams} from 'react-router-dom';
import {useShowIncidences} from '@/modules/auditorAudits/hooks/useShowIncidences';
import { useSelectAuditView, useShowAuditView } from '@/modules/auditorAudits/hooks/useSelectAuditView';
// import {Readable} from 'readable-stream';
import {FormData, Blob} from 'formdata-node';
import {FormDataEncoder} from 'form-data-encoder';
import {useResponsive} from '@/contexts/ResponsiveContext';
import ViewEditor from '@/components/Editor/viewEditor';

const { TextArea } = Input;

const Item = ({name, icon}) => {

  return (
    <div className={classNames(styles.incidentItem, 'mb-3 flex items-center justify-start')}>
      {icon} <div className="ml-2">{name}</div>
    </div>
  );
};

Item.propTypes = {
  name: PropTypes.string,
  icon: PropTypes.any,
};

const TemplateDetailsFooterActions = ({question, isCover, runAudit, sectionId}) => {
  const compRef = useRef(null);
  const {updateCurrentView} = useSelectAuditView();
  const {isActive: active, isNote, isCurrentView, isIncidences} = useShowAuditView(question?._id);
  const {t} = useTranslation('templates');
  const {id} = useParams();
  const {showAllIncidences} = useShowIncidences();
  const {isTablet} = useResponsive();

  const [addNote, setAddNote] = useState({
    visible: false,
    note: ''
  });
  const [globalNote, setGlobalNote] = useState({
    visible: false,
    note: runAudit ? question?.note || '' : ''
  });
  const [globalImages, setGlobalImages] = useState(runAudit ? question?.noteImages || [] : []);
  const [showIncidents, setShowIncidents] = useState(false);
  const [selectedTask, setSelectedTask] = useState([]);
  const {upload} = useCrudImage();
  const {createNote} = useCrudNote();

  /** On Upload image */
  const onUpload = useCallback(async (data) => {
    const form = new FormData();
    const file ={
      uid: uuidv4(),
      name: 'Image.png',
      status: 'done',
      url: data?.image || data,
    };
    let newImages;
    if (globalImages?.length) {
      newImages = [...globalImages, file];
    } else {
      newImages = [file];
    }
    setGlobalImages(prev => ([...prev, file]));
    form.set('noteImages', JSON.stringify(file));
    const encoder = new FormDataEncoder(form);
    const options = {
      headers: encoder.headers,
    };
    if (runAudit) await upload({auditId: id, sectionId, questionId: question?._id, options, noteImages: newImages, data: new Blob(encoder, {type: encoder.contentType})});
  }, [id, question?._id, runAudit, sectionId, upload, globalImages]);

  const {props: uploadImagesProps, handleChange, loading} = useUploadImage('', onUpload);

  const locale = {
    emptyText: t('noIncidencesForThisQuestion'),
  };

  /** Show global notes */
  const handleCreateOrUpdateGlobalNote = useCallback(() => {
    if (!active)
      setGlobalNote(prev => ({ ...prev, visible: !prev?.visible }));
    updateCurrentView?.(sectionId, question?._id, 'note');
  }, [active, updateCurrentView, sectionId, question]);

  /** Save global notes */
  const onSaveGlobalNote = useCallback(async (note) => {
    setGlobalNote(prev => ({note, visible: !prev?.visible}));
    updateCurrentView?.(sectionId, question?._id, '');
    if (runAudit) await createNote({auditId: id, sectionId, questionId: question?._id, note});
  }, [updateCurrentView, createNote, id, question?._id, runAudit, sectionId]);

  /** Save global notes */
  const onRemoveGlobalNote = useCallback(async () => {
    setGlobalNote(prev => ({note: '', visible: !prev?.visible}));
    updateCurrentView?.(sectionId, question?._id, '');
    if (runAudit) await createNote({auditId: id, sectionId, questionId: question?._id, note: ''});
  }, [updateCurrentView, createNote, id, question?._id, runAudit, sectionId]);

  const handleOnClickTask = useCallback((index) => {
    if(selectedTask.some(x => x === index)) setSelectedTask(selectedTask?.filter(x => x !== index));
    else setSelectedTask([...selectedTask, index]);
  }, [selectedTask]);

  useEffect(() => {
    if (showAllIncidences) {
      setShowIncidents(true);
    } else {
      setShowIncidents(false);
    }
  }, [showAllIncidences]);

  useEffect(() => {
    if (isCurrentView)
      setGlobalNote((prev) => ({
        visible: isNote || prev?.visible,
        note: runAudit ? question?.note || '' : ''
      }));
  }, [isCurrentView, isNote, question?.note, runAudit]);

  const onAddIncidents = useCallback(() => {
    if (!active)
      setShowIncidents(!showIncidents);
    updateCurrentView?.(sectionId, question?._id, 'incidences');
  }, [active, showIncidents, updateCurrentView, sectionId, question?._id]);

  if (isCover) {
    return null;
  }

  return (
    <div className="mt-3" ref={compRef}>
      {!addNote?.visible && addNote?.note && <div className="w-full flex items-center justify-center mb-3"><div style={{ width: '80%' }}>{addNote?.note}</div></div>}
      {addNote?.visible && <div className="w-full flex items-center justify-center mb-3">
        <div style={{ width: '80%' }}>
          <TextArea className="mb-3" placeholder={t('details.noteName')} value={addNote?.note} onChange={e => setAddNote(prev => ({...prev, note: e.target.value}))}/>
          <div className="flex">
            <Button disabled={!addNote?.note} onClick={() => {
              setAddNote(prev => ({...prev, visible: false}));
            }} type="primary" size="large" className="mr-4">{t('details.btnCreate')}</Button>
            <Button size="large" onClick={() => setAddNote(prev => ({...prev, visible: false}))}>{t('details.btnCancel')}</Button>
          </div>
        </div>
      </div>}

      {
        !active && showIncidents && <div className="h-full">
          <div className="font-medium text-black mb-3 font-bold">
            {t('tabs.incidents')}
          </div>
          <List
            itemLayout="vertical"
            size="large"
            locale={locale}
            dataSource={question?.issues}
            className={classNames(incidentStyles.viewList)}
            renderItem={(item, i) => (
              <TemplateDetailsIssuesHeader
                i={i}
                item={item}
                handleOnClickTask={handleOnClickTask}
                selectedTask={selectedTask}
                runAudit={runAudit}
                sectionId={sectionId}
                question={question}
              />
            )}
          />
        </div>
      }

      {globalImages?.length > 0 &&
            <div className="w-full flex items-center justify-start mb-3">
              <GlobalImages
                images={globalImages}
                onUpdate={setGlobalImages}
                sectionId={sectionId}
                questionId={question?._id}
                runAudit={runAudit}
              />
            </div>
      }

      {!globalNote?.visible || active
        ?
        <div className="w-full flex items-center justify-start mb-3">
          <div
            className="cursor-pointer"
            onClick={handleCreateOrUpdateGlobalNote}
          >
            <ViewEditor value={globalNote?.note} />
          </div>
        </div>
        :
        !active && <div className="w-full flex items-center justify-start mb-3">
          <GlobalNote
            note={globalNote?.note}
            onSave={onSaveGlobalNote}
            onCancel={handleCreateOrUpdateGlobalNote}
            onRemove={onRemoveGlobalNote}
            question={question}
          />
        </div>
      }

      <div className={classNames('flex items-center justify-between w-full flex-wrap gap-2', !runAudit && 'hidden')}>
        {question?.issues?.length > 0
          ? <div className="items-center justify-start mt-2">
            <Button type="text" icon={<FlagOutlined/>} onClick={onAddIncidents} disabled={active ? isIncidences : false}
              className={styles.btnAddIncident}>{!isTablet && t('details.incidence')}</Button>
          </div>
          : <div />
        }

        <div className="flex gap-2 flex-wrap">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={handleCreateOrUpdateGlobalNote}
            className={styles.btnAddIncident}
            disabled={ (active ? isNote : globalNote?.note && globalNote?.visible) }
          >
            {(!globalNote?.note && !isTablet) ? t('globalNote.create') : !isTablet && t('globalNote.edit')}
          </Button>

          <div className={styles.wrapperUploadImages}>
            <Upload
              {...uploadImagesProps}
              onChange={handleChange}
            >
              <Button
                type="text"
                icon={<PaperClipOutlined />}
                disabled={loading}
                loading={loading}
                className={styles.btnAddIncident}>
                {!isTablet && t('globalMedia.attach')}
              </Button>
            </Upload>
          </div>
        </div>
      </div>
    </div>
  );
};

TemplateDetailsFooterActions.propTypes = {
  question: PropTypes.object,
  isCover: PropTypes.bool,
  runAudit: PropTypes.bool,
  sectionId: PropTypes.string,
};

export default memo(TemplateDetailsFooterActions);
